// Carrier Activity tab — FE-SCRM-030 Step 4.
//
// Chronological Touchpoint history across every CarrierContact at
// this carrier. Fetched from the backend's carrier-scoped touchpoint
// endpoint (BE-SCRM-030 + this chunk's addition), ordered by
// occurred_at desc.

import { Badge } from "@/design-system";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { formatRelativeTouchpoint } from "./labels";
import { useCarrierTouchpointsQuery } from "./hooks";
import type { CarrierTouchpoint } from "./types";


interface Props {
  carrierId: string;
}


const KIND_LABEL: Record<string, string> = {
  meeting: "Meeting",
  call: "Call",
  email_in: "Email in",
  email_out: "Email out",
  site_visit: "Site visit",
  note: "Note",
};


export function CarrierActivityTab({ carrierId }: Props) {
  const query = useCarrierTouchpointsQuery(carrierId);

  if (query.isLoading) {
    return (
      <div data-testid="carrier-activity-tab">
        <DashboardLoadingState variant="list" rows={4} />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div data-testid="carrier-activity-tab">
        <DashboardErrorState
          title="Couldn't load activity"
          retry={() => query.refetch()}
        />
      </div>
    );
  }

  const rows = query.data?.rows ?? [];
  if (rows.length === 0) {
    return (
      <div data-testid="carrier-activity-tab">
        <DashboardEmptyState
          title="No touchpoints with anyone at this carrier yet"
          description={
            "Meetings, calls, and emails with this carrier's contacts "
            + "appear here once they're logged in BeneCRM."
          }
        />
      </div>
    );
  }

  return (
    <ol
      data-testid="carrier-activity-tab"
      className="flex flex-col gap-2"
    >
      {rows.map((tp) => (
        <ActivityRow key={tp.touchpoint_id} tp={tp} />
      ))}
    </ol>
  );
}


function ActivityRow({ tp }: { tp: CarrierTouchpoint }) {
  const rel = formatRelativeTouchpoint(tp.occurred_at);
  return (
    <li
      data-testid={`carrier-activity-row-${tp.touchpoint_id}`}
      className="flex flex-col gap-1 rounded-md border border-border-subtle bg-raised p-3"
    >
      <div className="flex flex-wrap items-center gap-2 text-xs text-text-tertiary">
        <Badge variant="neutral">
          {KIND_LABEL[tp.kind] ?? tp.kind}
        </Badge>
        <span>{rel ?? "—"}</span>
        {tp.carrier_contact_name && (
          <span className="text-text-secondary">
            with {tp.carrier_contact_name}
          </span>
        )}
        {tp.created_by_display_name && (
          <span>· logged by {tp.created_by_display_name}</span>
        )}
      </div>
      {tp.subject && (
        <p className="text-sm text-text-primary">{tp.subject}</p>
      )}
    </li>
  );
}
